import React from 'react';
import { cn } from '../../lib/utils';

/**
 * SkeletonLoader - Accessible loading states
 * Provides shimmer animation with proper ARIA labels
 */

export const Skeleton = ({ 
  className, 
  variant = 'default',
  ...props 
}) => {
  const variants = {
    default: 'rounded-md',
    circle: 'rounded-full',
    text: 'rounded h-4',
    card: 'rounded-3xl',
  };
  
  return (
    <div
      className={cn(
        'shimmer bg-gray-200 dark:bg-gray-700',
        variants[variant],
        className
      )}
      role="status"
      aria-label="Loading..."
      {...props}
    />
  );
};

// KPI Tile Skeleton
export const KpiTileSkeleton = ({ className }) => (
  <div 
    className={cn(
      'kpi-tile space-y-3',
      className
    )}
    role="status"
    aria-label="Loading KPI data..."
  >
    <div className="flex items-start justify-between">
      <div className="space-y-2 flex-1">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton variant="circle" className="h-10 w-10" />
    </div>
    <Skeleton className="h-12 w-full" />
    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
      <Skeleton className="h-3 w-full" />
    </div>
  </div>
);

// Card Skeleton
export const CardSkeleton = ({ className, lines = 3 }) => (
  <div 
    className={cn(
      'glass-card p-6 space-y-4',
      className
    )}
    role="status"
    aria-label="Loading content..."
  >
    <div className="space-y-2">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-3 w-24" />
    </div>
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          className="h-4" 
          style={{ width: `${100 - i * 15}%` }} 
        />
      ))}
    </div>
  </div>
);

// Table Row Skeleton
export const TableRowSkeleton = ({ columns = 5, className }) => (
  <tr 
    className={cn('animate-pulse', className)}
    role="status"
    aria-label="Loading table row..."
  >
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

// Dashboard Grid Skeleton
export const DashboardSkeleton = ({ widgets = 4 }) => (
  <div 
    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
    role="status"
    aria-label="Loading dashboard..."
  >
    {Array.from({ length: widgets }).map((_, i) => (
      <KpiTileSkeleton key={i} />
    ))}
  </div>
);

// Chart Skeleton
export const ChartSkeleton = ({ className, height = 'h-64' }) => (
  <div 
    className={cn(
      'glass-card p-6',
      className
    )}
    role="status"
    aria-label="Loading chart..."
  >
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Skeleton className="h-5 w-32" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-16" />
        </div>
      </div>
      <Skeleton className={cn('w-full', height)} />
    </div>
  </div>
);

export default Skeleton;
